import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { GalleryItem } from '../../lib/frontend-db';

const GalleryPage = () => {
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [title, setTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageTitle, setPageTitle] = useState('Bildergalerie');

  useEffect(() => {
    loadGallery();
    loadPageTitle();
  }, []);

  const loadPageTitle = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setPageTitle((settings as any).gallery_title || 'Galerie');
      }
    } catch (error) {
      console.error('Error loading page title:', error);
    }
  };

  const loadGallery = async () => {
    try {
      const items = await db.getGallery();
      setGallery(items);
    } catch (error) {
      console.error('Error loading gallery:', error);
      setError('Fehler beim Laden der Galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let imageUrl = editingItem?.image_url || '';

      if (selectedImage) {
        imageUrl = await db.storeImage(selectedImage);
      }

      if (editingItem) {
        await db.updateGalleryItem(editingItem.id, {
          ...editingItem,
          title,
          image_url: imageUrl,
          link_url: linkUrl || undefined,
        });
      } else {
        await db.createGalleryItem({
          title,
          image_url: imageUrl,
          link_url: linkUrl || undefined,
        });
      }

      resetForm();
      window.location.reload();
    } catch (error) {
      console.error('Error saving gallery item:', error);
      setError('Fehler beim Speichern des Galeriebildes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) {
      return;
    }

    try {
      await db.deleteGalleryItem(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      setError('Fehler beim Löschen des Bildes');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setTitle('');
    setLinkUrl('');
    setSelectedImage(null);
    setPreviewUrl('');
  };

  if (loading && gallery.length === 0) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">
          {editingItem ? 'Galeriebild bearbeiten' : 'Neues Galeriebild hinzufügen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Link (optional)</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              placeholder="https://beispiel.de"
            />
            <p className="mt-1 text-xs text-gray-500">Wenn ein Link angegeben wird, öffnet sich beim Klick auf das Bild dieser Link in einem neuen Tab</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Bild</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
              required={!editingItem?.image_url}
            />
            {(previewUrl || editingItem?.image_url) && (
              <div className="mt-2">
                <img
                  src={previewUrl || editingItem?.image_url}
                  alt="Vorschau"
                  className="w-full max-h-48 object-cover rounded"
                />
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Wird gespeichert...' : (editingItem ? 'Aktualisieren' : 'Erstellen')}
            </button>
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gallery.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold">{item.title}</h3>
                {item.link_url && (
                  <a 
                    href={item.link_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {item.link_url}
                  </a>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingItem(item);
                    setTitle(item.title);
                    setLinkUrl(item.link_url || '');
                    setPreviewUrl(item.image_url);
                  }}
                  className="p-1 text-green-700 hover:text-green-800"
                >
                  <Pencil size={20} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-1 text-red-600 hover:text-red-700"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
            <img
              src={item.image_url}
              alt={item.title}
              className="w-full h-48 object-cover rounded"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default GalleryPage;

